// src/utils/governanceExport.js
// AI Governance Export Utility - Enhancement 1: Export Governance Header
// For Citrin Cooperman Demo - Jan 20, 2026
// UPDATED: Multi-provider support with dynamic labels from aiConfig

import { Paragraph, TextRun, HeadingLevel, BorderStyle } from 'docx';
import { getProviderLabel } from '../config/aiProviderLabels';

/**
 * Generates AI Governance Header paragraphs for Word document exports
 * 
 * @param {Object} params - Header configuration
 * @param {string} params.threadId - Thread/Document ID (from question.id or businessUpdate.id)
 * @param {string} params.clientName - Client company name
 * @param {string} params.advisorName - FCFO advisor name
 * @param {string} params.advisorEmail - FCFO email address
 * @param {Date|null} params.dateRangeStart - Start of content date range (optional)
 * @param {Date|null} params.dateRangeEnd - End of content date range (optional)
 * @param {Date} params.exportTimestamp - When the export was generated
 * @param {string} params.contentType - Type of content: 'Q&A', 'SavvaiSignals', 'Decision Memo', 'Industry Insights', etc.
 * @param {Date|null} params.quickBooksLastSync - Last QuickBooks sync timestamp (optional)
 * @param {Array|null} params.aiProviders - AI providers used, array of {name, role} objects
 * @returns {Paragraph[]} Array of docx Paragraph objects for the governance header
 */
export const generateGovernanceHeader = ({
  threadId,
  clientName,
  advisorName,
  advisorEmail,
  dateRangeStart = null,
  dateRangeEnd = null,
  exportTimestamp = new Date(),
  contentType = 'Export',
  quickBooksLastSync = null,
  aiProviders = [],
}) => {
  const paragraphs = [];

  // Format dates consistently
  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) date = date.toDate(); // Handle Firestore timestamps
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateOnly = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) date = date.toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Header title with border
  paragraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'AI GOVERNANCE & AUDIT TRAIL',
          bold: true,
          size: 24, // 12pt
          color: '005DAA', // Savvai brand blue
        }),
      ],
      spacing: { before: 0, after: 100 },
      border: {
        bottom: {
          color: '005DAA',
          size: 12,
          style: BorderStyle.SINGLE,
        },
      },
    })
  );

  // Disclosure statement - UPDATED: Softer language per liability review
  paragraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'This document may include AI-assisted analysis generated via SavvaiEdge™. Your advisory team remains responsible for reviewing and validating all content before client delivery.',
          italics: true,
          size: 18, // 9pt
          color: '666666',
        }),
      ],
      spacing: { before: 100, after: 200 },
    })
  );

  // Build metadata rows
  const metadataRows = [];

  // Row 1: Thread ID and Content Type
  if (threadId) {
    metadataRows.push({ label: 'Thread ID', value: threadId });
  }
  metadataRows.push({ label: 'Content Type', value: contentType });

  // Row 2: Client and Advisor
  if (clientName) {
    metadataRows.push({ label: 'Client', value: clientName });
  }
  if (advisorName) {
    metadataRows.push({ label: 'Advisor', value: advisorName });
  }
  if (advisorEmail) {
    metadataRows.push({ label: 'Advisor Email', value: advisorEmail });
  }

  // Row 3: Date Range (if applicable)
  if (dateRangeStart || dateRangeEnd) {
    const rangeStr = `${formatDateOnly(dateRangeStart)} to ${formatDateOnly(dateRangeEnd)}`;
    metadataRows.push({ label: 'Date Range', value: rangeStr });
  }

  // Row 4: Export timestamp
  metadataRows.push({ label: 'Export Generated', value: formatDate(exportTimestamp) });

  // Row 5: AI Providers (if available) - supports multiple providers with roles
  if (aiProviders && aiProviders.length > 0) {
    aiProviders.forEach((provider, index) => {
      const label = index === 0 ? 'AI Providers' : '';
      const bullet = '• ';
      const value = provider.role 
        ? `${bullet}${provider.name} (${provider.role})`
        : `${bullet}${provider.name}`;
      metadataRows.push({ label, value });
    });
  }

  // Row 6: Financial Data Currency (if QuickBooks connected)
  if (quickBooksLastSync) {
    metadataRows.push({
      label: 'Financial Data As Of',
      value: formatDate(quickBooksLastSync),
    });
  }

  // Generate metadata paragraphs
  metadataRows.forEach((row) => {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({ text: row.label ? `${row.label}: ` : '            ', bold: !!row.label, size: 20 }),
          new TextRun({ text: row.value, size: 20 }),
        ],
        spacing: { after: 50 },
      })
    );
  });

  // Separator line
  paragraphs.push(
    new Paragraph({
      children: [new TextRun({ text: '' })],
      spacing: { before: 200, after: 200 },
      border: {
        bottom: {
          color: 'CCCCCC',
          size: 6,
          style: BorderStyle.SINGLE,
        },
      },
    })
  );

  return paragraphs;
};

/**
 * Generates a simple governance footer for document end
 * @returns {Paragraph[]} Array of docx Paragraph objects
 */
export const generateGovernanceFooter = () => {
  return [
    new Paragraph({
      children: [new TextRun({ text: '' })],
      spacing: { before: 400 },
      border: {
        top: {
          color: 'CCCCCC',
          size: 6,
          style: BorderStyle.SINGLE,
        },
      },
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: 'SavvaiEdge™ AI Governance Notice: ',
          bold: true,
          size: 16,
          color: '666666',
        }),
        new TextRun({
          // UPDATED: Softer language - "may contain" instead of "contains"
          text: 'This document may contain AI-assisted analysis. While we strive for accuracy, AI outputs should be validated against source data. For questions about AI methodology or data sources, contact your advisory team.',
          size: 16,
          color: '666666',
          italics: true,
        }),
      ],
      spacing: { before: 100, after: 100 },
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: '© 2026 Savvai Inc. | Powered by SavvaiEdge™ Strategic Intelligence Platform',
          size: 16,
          color: '999999',
        }),
      ],
      spacing: { after: 0 },
    }),
  ];
};

/**
 * Helper to extract governance metadata from an item
 * Works with questions, businessUpdates, Decision Memos, etc.
 * 
 * @param {Object} item - The data item being exported
 * @param {Object} client - The client document
 * @param {Object} user - The current user/FCFO
 * @param {string} contentType - Type label for the content
 * @returns {Object} Params ready for generateGovernanceHeader
 */
export const extractGovernanceMetadata = (item, client, user, contentType = 'Export') => {
  // Build AI providers array from item metadata
  const aiProviders = [];
  
  // Primary synthesis provider (stored as human-readable label in item.provider)
  if (item?.provider) {
    aiProviders.push({ 
      name: item.provider, 
      role: 'synthesis' 
    });
  }
  
  // Research provider from aiAuditMetadata (stored as model key, needs label lookup)
  const researchModelKey = item?.aiAuditMetadata?.researchModel;
  if (researchModelKey) {
    aiProviders.push({ 
      name: getProviderLabel(researchModelKey), 
      role: 'real-time external research' 
    });
  }
  
  // Additional models (e.g., fallback or intermediate processing)
  const additionalModels = item?.aiAuditMetadata?.additionalModels;
  if (additionalModels && Array.isArray(additionalModels) && additionalModels.length > 0) {
    additionalModels.forEach(modelKey => {
      aiProviders.push({
        name: getProviderLabel(modelKey),
        role: 'intermediate processing'
      });
    });
  }

  return {
    threadId: item?.id || null,
    clientName: client?.officialCompanyName || client?.name || client?.displayName || 'Unknown Client',
    advisorName: user?.displayName || user?.name || user?.email?.split('@')[0] || 'Unknown Advisor',
    advisorEmail: user?.email || null,
    dateRangeStart: item?.createdAt || null,
    dateRangeEnd: item?.answeredAt || item?.updatedAt || item?.createdAt || null,
    exportTimestamp: new Date(),
    contentType,
    quickBooksLastSync: client?.quickbooksLastFetchedAt || null,
    aiProviders,
  };
};

export default {
  generateGovernanceHeader,
  generateGovernanceFooter,
  extractGovernanceMetadata,
};